import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ReactCompareSlider, ReactCompareSliderImage } from "react-compare-slider";
import { ShieldCheck, Award, Users, FileCheck, ArrowRight, Phone, Star } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { api, imgUrl, isVideo, track } from "../lib/api";
import { Reveal, MaskedLine, Overline } from "../components/Reveal";
import { formatPhone } from "../components/Header";
import { Seo } from "../components/Seo";
import { PAGE_SEO } from "../lib/seo";

const HERO_IMG = "https://images.unsplash.com/photo-1613545325278-f24b0cae1224?w=1920&q=75";

const TRUST_ICONS = [ShieldCheck, Award, Users, FileCheck];

export default function Home() {
  const { settings, services, L } = useSite();
  const [gallery, setGallery] = useState([]);
  const [ba, setBa] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [slide, setSlide] = useState(0);
  const heroImages = settings.hero_images?.length ? settings.hero_images.map(imgUrl) : [HERO_IMG];
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  useEffect(() => {
    if (heroImages.length < 2) return;
    const t = setInterval(() => setSlide((s) => (s + 1) % heroImages.length), 6000);
    return () => clearInterval(t);
  }, [heroImages.length]);

  useEffect(() => {
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
    api.get("/before_after").then((r) => setBa(r.data.filter((p) => !isVideo(p.before_image) && !isVideo(p.after_image)))).catch(() => {});
    api.get("/testimonials").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  const featured = gallery.filter((g) => g.featured && !/\.(mp4|mov|webm)$/i.test(g.image || "")).slice(0, 3);

  return (
    <main data-testid="home-page">
      <Seo title={PAGE_SEO.home.title} description={PAGE_SEO.home.description} path="/" />
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden bg-[#0B1B3D]">
        <motion.div className="absolute inset-0" style={{ y: bgY }}>
          {heroImages.map((src, i) => (
            <img key={src} src={src} alt="Noyola Construction — professional service"
              className={`absolute inset-0 w-full h-[115%] object-cover transition-opacity duration-1000 ${i === slide % heroImages.length ? "opacity-100" : "opacity-0"}`} />
          ))}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B3D] via-[#0B1B3D]/60 to-black/30" />
        </motion.div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 pb-20 md:pb-28 pt-40 w-full">
          <MaskedLine delay={0.1}>
            <p className="uppercase tracking-[0.3em] text-blue-300 font-heading font-bold text-xs md:text-sm mb-6">
              {L.hero.overline}
            </p>
          </MaskedLine>
          <h1 className="font-heading font-extrabold text-white tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-7xl max-w-4xl">
            <MaskedLine delay={0.25}>{L.hero.line1}</MaskedLine>
            <MaskedLine delay={0.4}>{L.hero.line2}</MaskedLine>
            <MaskedLine delay={0.55}><span className="text-[#4D8DFF]">{L.hero.line3}</span></MaskedLine>
          </h1>
          <MaskedLine delay={0.75}>
            <p className="text-slate-300 text-base md:text-lg max-w-2xl mt-8 leading-relaxed">
              {L.hero.subtitle}
            </p>
          </MaskedLine>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.7 }}
            className="flex flex-col sm:flex-row gap-4 mt-10">
            <Link to="/estimate" data-testid="hero-estimate-btn" className="btn-primary">{L.hero.cta1}</Link>
            <a href={`tel:${settings.phone}`} data-testid="hero-call-btn" onClick={() => track("phone_click", { location: "hero" })} className="btn-outline">
              <Phone className="w-4 h-4" /> {L.hero.cta2} {formatPhone(settings.phone)}
            </a>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2, duration: 0.8 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-14 border-t border-white/15 pt-8">
            {L.hero.trust.map((label, i) => {
              const Icon = TRUST_ICONS[i];
              return (
                <div key={label} className="flex items-center gap-3">
                  <Icon className="w-5 h-5 text-[#4D8DFF] shrink-0" />
                  <span className="text-white font-heading font-semibold text-xs md:text-sm tracking-wide">{label}</span>
                </div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="bg-white border-b border-slate-200 py-6 overflow-hidden">
        <Marquee speed={30} autoFill gradient={false}>
          {L.marquee.map((s) => (
            <span key={s} className="font-heading font-bold uppercase tracking-[0.2em] text-slate-800 text-sm md:text-base mx-8 flex items-center gap-8">
              {s} <span className="text-[#005AE0]">•</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* MANIFESTO */}
      <section className="bg-[#0B1B3D] relative grain py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <Overline light>{L.manifesto.overline}</Overline>
            <h2 className="font-heading font-extrabold text-white tracking-tight text-3xl md:text-5xl max-w-3xl leading-tight">
              {L.manifesto.title[0]}<br />{L.manifesto.title[1]}
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-14 mt-20">
            {L.manifesto.items.map((m, i) => (
              <Reveal key={m.t} delay={i * 0.08}>
                <div className="border-t border-white/15 pt-8 flex gap-6">
                  <span className="font-heading font-extrabold text-4xl md:text-5xl text-[#005AE0]">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h3 className="font-heading font-bold text-white text-xl mb-3">{m.t}</h3>
                    <p className="text-slate-400 leading-relaxed text-sm md:text-base">{m.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-16">
            <Link to="/about" data-testid="home-about-link" className="inline-flex items-center gap-2 text-blue-300 font-heading font-bold uppercase tracking-widest text-sm hover:text-white transition-colors">
              {L.manifesto.more} <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <Overline>{L.servicesSec.overline}</Overline>
              <h2 className="font-heading font-extrabold text-[#0B1B3D] tracking-tight text-3xl md:text-5xl">{L.servicesSec.title}</h2>
            </div>
            <Link to="/services" data-testid="home-all-services-link" className="btn-outline-dark !py-3 !px-6 !text-xs self-start md:self-auto">{L.servicesSec.all}</Link>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.slice(0, 6).map((s, i) => (
              <Reveal key={s.id} delay={i * 0.06}>
                <Link to={`/services/${s.slug}`} data-testid={`home-service-${s.slug}`} className="group block bg-white border border-slate-200 hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                  <div className="h-56 overflow-hidden">
                    <img src={`${imgUrl(s.image)}?w=800&q=70`} alt={s.title} loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-7">
                    <h3 className="font-heading font-bold text-[#0B1B3D] text-xl mb-2">{s.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed mb-5">{s.description}</p>
                    <span className="inline-flex items-center gap-2 text-[#005AE0] font-heading font-bold uppercase tracking-widest text-xs">
                      {L.servicesSec.request} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-200" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BEFORE / AFTER */}
      {ba.length > 0 && (
        <section className="py-24 md:py-32 bg-white">
          <div className="max-w-6xl mx-auto px-5 lg:px-8">
            <Reveal className="mb-14">
              <Overline>{L.baSec.overline}</Overline>
              <h2 className="font-heading font-extrabold text-[#0B1B3D] tracking-tight text-3xl md:text-5xl">{L.baSec.title}</h2>
            </Reveal>
            <Reveal>
              <div data-testid="home-before-after-slider">
                <ReactCompareSlider
                  itemOne={<ReactCompareSliderImage src={`${imgUrl(ba[0].before_image)}?w=1400&q=75`} alt={`${ba[0].title} — before`} />}
                  itemTwo={<ReactCompareSliderImage src={`${imgUrl(ba[0].after_image)}?w=1400&q=75`} alt={`${ba[0].title} — after`} />}
                  style={{ height: "min(70vh, 620px)" }}
                />
              </div>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mt-6">
                <div>
                  <h3 className="font-heading font-bold text-[#0B1B3D] text-xl">{ba[0].title}</h3>
                  <p className="text-slate-500 text-sm">{ba[0].service}{ba[0].location ? ` • ${ba[0].location}` : ""}</p>
                </div>
                <Link to="/before-after" data-testid="home-ba-link" className="btn-outline-dark !py-3 !px-6 !text-xs self-start">{L.baSec.viewAll}</Link>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* FEATURED PROJECTS */}
      {featured.length > 0 && (
        <section className="py-24 md:py-32 bg-[#F8FAFC]">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
              <div>
                <Overline>{L.projectsSec.overline}</Overline>
                <h2 className="font-heading font-extrabold text-[#0B1B3D] tracking-tight text-3xl md:text-5xl">{L.projectsSec.title}</h2>
              </div>
              <Link to="/projects" data-testid="home-projects-link" className="btn-outline-dark !py-3 !px-6 !text-xs self-start md:self-auto">{L.projectsSec.fullGallery}</Link>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featured.map((g, i) => (
                <Reveal key={g.id} delay={i * 0.08}>
                  <div className="group relative overflow-hidden h-80">
                    <img src={`${imgUrl(g.image)}?w=800&q=70`} alt={g.title} loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B3D]/85 to-transparent flex flex-col justify-end p-6">
                      <p className="text-blue-300 font-heading text-xs uppercase tracking-widest mb-1">{g.category}</p>
                      <h3 className="font-heading font-bold text-white text-lg">{g.title}</h3>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* REVIEWS */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="mb-14">
            <Overline>{L.reviewsSec.overline}</Overline>
            <h2 className="font-heading font-extrabold text-[#0B1B3D] tracking-tight text-3xl md:text-5xl">{L.reviewsSec.title}</h2>
          </Reveal>
          {reviews.length === 0 ? (
            <Reveal>
              <div className="border border-slate-200 bg-[#F8FAFC] p-10 md:p-14 text-center max-w-2xl mx-auto">
                <div className="flex justify-center gap-1 mb-4">{[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 text-[#005AE0]" />)}</div>
                <p className="text-slate-600 leading-relaxed">
                  {L.reviewsSec.emptyHome}
                </p>
              </div>
            </Reveal>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {reviews.slice(0, 3).map((r, i) => (
                <Reveal key={r.id} delay={i * 0.08}>
                  <div className="border border-slate-200 p-8 h-full">
                    <div className="flex gap-1 mb-4">{[...Array(r.rating || 5)].map((_, j) => <Star key={j} className="w-4 h-4 text-[#005AE0] fill-[#005AE0]" />)}</div>
                    <p className="text-slate-600 leading-relaxed mb-6">“{r.text}”</p>
                    <p className="font-heading font-bold text-[#0B1B3D]">{r.name}</p>
                    <p className="text-xs text-slate-400 uppercase tracking-widest">{r.source}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#005AE0] py-20 md:py-28 relative grain">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-heading font-extrabold text-white tracking-tight text-3xl md:text-5xl mb-6">
              {L.cta.title}
            </h2>
            <p className="text-blue-100 text-lg mb-10 max-w-xl mx-auto">{L.cta.subtitle} {settings.service_area || ""}.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/estimate" data-testid="cta-estimate-btn" className="btn-primary !bg-white !text-[#0B1B3D] hover:!bg-slate-100">{L.cta.btn1}</Link>
              <a href={`tel:${settings.phone}`} data-testid="cta-call-btn" onClick={() => track("phone_click", { location: "cta" })} className="btn-outline">
                <Phone className="w-4 h-4" /> {L.cta.btn2}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
